import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ControlerEnum } from '../enums/controler.enum';
import { Observable } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { ICreateUser, ICreateUserData, IDeleteUser, IGetUser, IUpdateUser, IUpdateUserData, IUserList, IUserListResult, IUserLogin, IUserLoginData, IUserPagination, IUserResult } from '../interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apiBaseUrl: string = environment.apiBaseUrl;
  private http = inject(HttpClient);

  login(model: IUserLoginData): Observable<HttpResponse<IUserLogin>> {
    return this.http.post<IUserLogin>(
      `${this.apiBaseUrl}/${ControlerEnum.User}/login`,
      model,
      { observe: 'response' },
    );
  }

  logout(refreshToken: string): Observable<HttpResponse<unknown>> {
    const model = {
      refreshToken: refreshToken,
    };

    return this.http.post<unknown>(
      `${this.apiBaseUrl}/${ControlerEnum.User}/logout`,
      model,
      { observe: 'response' },
    );
  }

  refreshToken(refreshToken: string): Observable<HttpResponse<string>> {
    const model = {
      refreshToken: refreshToken,
    };

    return this.http.post<string>(
      `${this.apiBaseUrl}/${ControlerEnum.User}/refresh-token`,
      model,
      { observe: 'response' },
    );
  }

  getUsers(data: IUserPagination, vendorGuid: string | null): Observable<HttpResponse<IUserList>> {
    let query = `?pageSize=${data.pageSize}&pageNumber=${data.pageNumber}`;

    if (data.fullName) {
      query += `&fullName=${data.fullName}`;
    }

    if (data.countryName) {
      query += `&countryName=${data.countryName}`;
    }

    if (data.userName) {
      query += `&userName=${data.userName}`;
    }

    if (data.roles) {
      data.roles.forEach((role: any) => {
        query += `&roles=${role.name}`;
      });
    }

    if (data.guid) {
      query += `&guid=${data.guid}`;
    }

    if (vendorGuid) {
      query += `&vendorGuid=${vendorGuid}`;
    }

    return this.http.get<IUserList>(
      `${this.apiBaseUrl}/${ControlerEnum.User}${query}`,
      { observe: 'response' },
    );
  }

  getUserById(guid: string): Observable<HttpResponse<IGetUser>> {
    return this.http.get<IGetUser>(
      `${this.apiBaseUrl}/${ControlerEnum.User}/${guid}`,
      { observe: 'response' },
    );
  }

  getUsersByCountry(coutnryId: number): Observable<HttpResponse<IUserListResult>> {
    return this.http.get<IUserListResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Usercountry}/${coutnryId}`,
      { observe: 'response' },
    );
  }

  createUser(
    model: ICreateUserData,
  ): Observable<HttpResponse<IUserResult>> {
    const createModel: ICreateUser = {
      countryId: model.countryId,
      fullName: model.fullName,
      username: model.username,
      isCountryAdmin: model.isCountryAdmin,
      isSuperAdmin: model.isSuperAdmin,
      isNLBUser: model.isNLBUser,
      vendors: model.vendors,
      password: model.password ? CryptoJS.SHA256(
        CryptoJS.enc.Utf16LE.parse(model.password),
      )
        .toString(CryptoJS.enc.Hex)
        .toLowerCase() : null
    };

    return this.http.post<IUserResult>(
      `${this.apiBaseUrl}/${ControlerEnum.User}`,
      createModel,
      { observe: 'response' },
    );
  }

  updateUser(
    model: IUpdateUserData,
  ): Observable<HttpResponse<IUserResult>> {
    const updateModel: IUpdateUser = {
      countryId: model.countryId,
      guid: model.guid,
      fullName: model.fullName,
      username: model.username,
      isCountryAdmin: model.isCountryAdmin,
      isSuperAdmin: model.isSuperAdmin,
      isNLBUser: model.isNLBUser,
      isPasswordChanged: model.isPasswordChanged,
      vendors: model.vendors,
      password: model.password && model.isPasswordChanged ? CryptoJS.SHA256(
        CryptoJS.enc.Utf16LE.parse(model.password),
      )
        .toString(CryptoJS.enc.Hex)
        .toLowerCase() : null
    };

    return this.http.put<IUserResult>(
      `${this.apiBaseUrl}/${ControlerEnum.User}`,
      updateModel,
      { observe: 'response' },
    );
  }

  deleteUser(guid: string): Observable<HttpResponse<IDeleteUser>> {
    return this.http.delete<IDeleteUser>(
      `${this.apiBaseUrl}/${ControlerEnum.User}/${guid}`,
      { observe: 'response' },
    );
  }
}
